import { useState, useRef, useEffect, Children, ReactNode, useCallback } from 'react';
import { cn } from '@/lib/utils';

interface ResponsiveLayoutProps {
  children: ReactNode;
  className?: string;
}

export const ResponsiveLayout = ({ children, className }: ResponsiveLayoutProps) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [startY, setStartY] = useState(0);
  const [currentTranslate, setCurrentTranslate] = useState(0);
  const [prevTranslate, setPrevTranslate] = useState(0);
  const [isHorizontalSwipe, setIsHorizontalSwipe] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  
  const childrenArray = Children.toArray(children);
  const totalPages = childrenArray.length;

  const setSliderPosition = useCallback(() => {
    if (containerRef.current) {
      const translateX = currentPage * (-100 / totalPages);
      containerRef.current.style.transform = `translateX(${translateX}%)`;
    }
  }, [currentPage, totalPages]);

  const setPositionByIndex = useCallback((index: number) => {
    setCurrentPage(index);
    setPrevTranslate(index * (-100 / totalPages));
    setCurrentTranslate(index * (-100 / totalPages));
  }, [totalPages]);

  const handleTouchStart = (e: React.TouchEvent) => {
    setIsDragging(true);
    setStartX(e.touches[0].clientX);
    setStartY(e.touches[0].clientY);
    setIsHorizontalSwipe(false);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;
    
    const currentX = e.touches[0].clientX;
    const currentY = e.touches[0].clientY;
    const diffX = currentX - startX;
    const diffY = currentY - startY;
    
    // Determine swipe direction on first move
    if (!isHorizontalSwipe) {
      const isHorizontal = Math.abs(diffX) > Math.abs(diffY);
      setIsHorizontalSwipe(isHorizontal);
      
      // If it's not a horizontal swipe, don't handle it
      if (!isHorizontal) return;
    }
    
    // Only handle horizontal swipes
    if (!isHorizontalSwipe) return;
    
    // Prevent default to avoid scrolling
    e.preventDefault();
    
    const containerWidth = containerRef.current?.offsetWidth || 0;
    const movePercent = (diffX / containerWidth) * 100;
    
    setCurrentTranslate(prevTranslate + movePercent);
    
    if (containerRef.current) {
      containerRef.current.style.transform = `translateX(${currentTranslate}%)`;
    }
  };

  const handleTouchEnd = () => {
    if (!isDragging) return;
    
    setIsDragging(false);
    
    // Only handle page changes for horizontal swipes
    if (isHorizontalSwipe) {
      const movedBy = currentTranslate - prevTranslate;
      const threshold = 25 / totalPages; // 25% of viewport width
      
      if (movedBy < -threshold && currentPage < totalPages - 1) {
        setCurrentPage(currentPage + 1);
      } else if (movedBy > threshold && currentPage > 0) {
        setCurrentPage(currentPage - 1);
      }
    }
    
    // Reset to current page position
    setSliderPosition();
    setIsHorizontalSwipe(false);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setStartX(e.clientX);
    setStartY(e.clientY);
    setIsHorizontalSwipe(false);
    e.preventDefault();
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    
    const currentX = e.clientX;
    const currentY = e.clientY;
    const diffX = currentX - startX;
    const diffY = currentY - startY;
    
    // Determine swipe direction on first move
    if (!isHorizontalSwipe) {
      const isHorizontal = Math.abs(diffX) > Math.abs(diffY);
      setIsHorizontalSwipe(isHorizontal);
      
      if (!isHorizontal) return;
    }
    
    if (!isHorizontalSwipe) return;
    
    const containerWidth = containerRef.current?.offsetWidth || 0;
    const movePercent = (diffX / containerWidth) * 100;
    
    setCurrentTranslate(prevTranslate + movePercent);
    
    if (containerRef.current) {
      containerRef.current.style.transform = `translateX(${currentTranslate}%)`;
    }
  };

  const handleMouseUp = () => {
    if (!isDragging) return;
    
    setIsDragging(false);
    
    if (isHorizontalSwipe) {
      const movedBy = currentTranslate - prevTranslate;
      const threshold = 25 / totalPages; // 25% of viewport width
      
      if (movedBy < -threshold && currentPage < totalPages - 1) {
        setCurrentPage(currentPage + 1);
      } else if (movedBy > threshold && currentPage > 0) {
        setCurrentPage(currentPage - 1);
      }
    }
    
    setSliderPosition();
    setIsHorizontalSwipe(false);
  };

  useEffect(() => {
    setSliderPosition();
  }, [currentPage, setSliderPosition]);

  useEffect(() => {
    setPrevTranslate(currentPage * (-100 / totalPages));
    setCurrentTranslate(currentPage * (-100 / totalPages));
  }, [currentPage, totalPages]);

  // Handle mouse events outside the container
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (isDragging) {
        handleMouseUp();
      }
    };

    const handleGlobalMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        handleMouseMove(e as any);
      }
    };

    document.addEventListener('mouseup', handleGlobalMouseUp);
    document.addEventListener('mousemove', handleGlobalMouseMove);

    return () => {
      document.removeEventListener('mouseup', handleGlobalMouseUp);
      document.removeEventListener('mousemove', handleGlobalMouseMove);
    };
  }, [isDragging, handleMouseUp, handleMouseMove]);

  const goToPage = (index: number) => {
    setPositionByIndex(index);
  };

  return (
    <div className={cn("h-full", className)}>
      {/* Desktop Layout: Vertical sections */}
      <div className="hidden md:block h-full overflow-y-auto">
        {childrenArray.map((child, index) => (
          <div key={index}>
            {child}
          </div>
        ))}
      </div>

      {/* Mobile Layout: Horizontal swipeable pages */}
      <div className="md:hidden h-full flex flex-col">
        <div 
          className="flex-1 overflow-hidden"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
        >
          <div 
            ref={containerRef}
            className="flex h-full transition-transform duration-300 ease-out"
            style={{ 
              width: `${totalPages * 100}%`,
              transform: `translateX(${currentPage * (-100 / totalPages)}%)`,
            }}
          >
            {childrenArray.map((child, index) => (
              <div 
                key={index}
                className="w-full h-full flex-shrink-0"
                style={{ width: `${100 / totalPages}%` }}
              >
                {child}
              </div>
            ))}
          </div>
        </div>
        
        {/* Page Indicators */}
        <div className="flex justify-center py-3 bg-background border-t border-border">
          {childrenArray.map((_, index) => (
            <button
              key={index}
              onClick={() => goToPage(index)}
              className={cn(
                "w-2 h-2 rounded-full mx-1 transition-colors duration-200",
                index === currentPage
                  ? "bg-primary"
                  : "bg-muted-foreground/30 hover:bg-muted-foreground/50"
              )}
            />
          ))}
        </div>
      </div>
    </div>
  );
};